import { Alert } from 'react-bootstrap';
import {
  CONSTRAINT_CATEGORY_LABELS,
  CONSTRAINT_DIAGNOSTICS_MESSAGES,
  ConstraintBound,
  ConstraintDiagnosticsStatus,
  ConstraintDiagnosticsStatusType,
  ConstraintViolation
} from './constraint-diagnostics-constants';
import { formatConstraintAmount } from './constraint-diagnostics-utils';

interface ConstraintFailureDetailsProps {
  status: ConstraintDiagnosticsStatusType;
  violationCount?: number;
  violations?: ConstraintViolation[];
}

const ViolationDetails = ({ violation }: { violation: ConstraintViolation }) => (
  <li>
    <p className='mb-2'>
      <strong>
        {CONSTRAINT_CATEGORY_LABELS[violation.category]} rule: {violation.name}
      </strong>
      {violation.networks.length > 0 && ` (${violation.networks.join(', ')})`}
    </p>
    <dl className='d-flex flex-wrap gap-4 mb-0'>
      <div>
        <dt>
          {violation.bound === ConstraintBound.MINIMUM ? 'Requested minimum' : 'Allowed maximum'}
        </dt>
        <dd className='mb-0'>{formatConstraintAmount(violation.limit, violation.category)}</dd>
      </div>
      <div>
        <dt>Attempted amount</dt>
        <dd className='mb-0'>{formatConstraintAmount(violation.actual, violation.category)}</dd>
      </div>
      <div>
        <dt>{violation.bound === ConstraintBound.MINIMUM ? 'Shortfall' : 'Excess'}</dt>
        <dd className='mb-0'>{formatConstraintAmount(violation.difference, violation.category)}</dd>
      </div>
    </dl>
  </li>
);

export const ConstraintFailureDetails = ({
  status,
  violations = [],
  violationCount = violations.length
}: ConstraintFailureDetailsProps) => (
  <Alert variant='warning' className='mb-0' aria-live='polite'>
    <Alert.Heading>No valid plan found</Alert.Heading>
    {status === ConstraintDiagnosticsStatus.VIOLATIONS_FOUND && violations.length > 0 ? (
      <>
        <p className='mb-1'>Rules not met when the solver stopped</p>
        <p>These values come from one unsuccessful solver attempt.</p>
        <ul className='list-unstyled d-grid gap-3'>
          {violations.map(violation => (
            <ViolationDetails
              key={`${violation.matrixRow}:${violation.bound}`}
              violation={violation}
            />
          ))}
        </ul>
        {violationCount > violations.length && (
          <p>
            Showing {violations.length.toLocaleString('en-US')} of{' '}
            {violationCount.toLocaleString('en-US')} rule violations from this attempt.
          </p>
        )}
        <p className='mb-0'>
          Review these rules together; the values from this attempt do not identify every possible
          conflict.
        </p>
      </>
    ) : (
      <p className='mb-0'>
        {
          CONSTRAINT_DIAGNOSTICS_MESSAGES[
            status === ConstraintDiagnosticsStatus.VIOLATIONS_FOUND
              ? ConstraintDiagnosticsStatus.NOT_EXPLAINED
              : status
          ]
        }
      </p>
    )}
  </Alert>
);
